import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthApiService,
  AuthUser,
  LoginCredentials,
  LoginV2Response,
  PersistedToken,
} from '@fy-web-2/data-access-auth';
import { UserModule } from '@fy-web-2/shared-util';
import { firstValueFrom } from 'rxjs';
import { AuthStore } from './auth.store';

const STORAGE_KEY_TOKEN = 'fy-web-2.authToken';
const STORAGE_KEY_USER_MODULES = 'fy-web-2.userModules';

/**
 * Facade for all auth operations. Components and guards/interceptors should
 * only depend on this — never on `AuthApiService` or `AuthStore` directly.
 */
@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly api = inject(AuthApiService);
  private readonly store = inject(AuthStore);
  private readonly router = inject(Router);

  readonly status = this.store.status;
  readonly user = this.store.user;
  readonly token = this.store.token;
  readonly userModules = this.store.userModules;
  readonly accessToken = this.store.accessToken;
  readonly error = this.store.error;
  readonly isAuthenticated = this.store.isAuthenticated;

  /**
   * Restore session from `localStorage`, if present and not expired.
   * Call once during app bootstrap (see `provideAuthBootstrap`).
   */
  bootstrap(): void {
    const persisted = this.readPersistedToken();
    if (!persisted) return;
    if (persisted.expiresAt <= Date.now()) {
      this.clearPersistedSession();
      return;
    }
    const userModules = this.readPersistedUserModules();
    this.store.setAuthenticated(persisted, { username: '' }, userModules);
  }

  async login(credentials: LoginCredentials): Promise<boolean> {
    this.store.setLoading();
    try {
      const response = await firstValueFrom(this.api.loginV2(credentials));
      const token = this.toPersistedToken(response);
      const user: AuthUser = {
        username: credentials.username,
        userCategory: response.user_category,
      };
      const userModules = response.user_modules ?? [];

      this.writePersistedToken(token);
      this.writePersistedUserModules(userModules);
      this.store.setAuthenticated(token, user, userModules);
      await this.router.navigateByUrl('/dashboard');
      return true;
    } catch (err) {
      this.store.setError(this.toErrorMessage(err));
      return false;
    }
  }

  async logout(): Promise<void> {
    this.clearPersistedSession();
    this.store.reset();
    await this.router.navigateByUrl('/login');
  }

  private toPersistedToken(response: LoginV2Response): PersistedToken {
    return {
      accessToken: response.access_token,
      tokenType: response.token_type,
      refreshToken: response.refresh_token,
      expiresAt: Date.now() + response.expires_in * 1000,
      scope: response.scope,
      userCategory: response.user_category,
    };
  }

  private readPersistedToken(): PersistedToken | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_TOKEN);
      if (!raw) return null;
      return JSON.parse(raw) as PersistedToken;
    } catch {
      return null;
    }
  }

  private writePersistedToken(token: PersistedToken): void {
    try {
      localStorage.setItem(STORAGE_KEY_TOKEN, JSON.stringify(token));
    } catch {
      /* storage may be unavailable (private mode); session-only fallback */
    }
  }

  private readPersistedUserModules(): readonly UserModule[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_USER_MODULES);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as UserModule[]) : [];
    } catch {
      return [];
    }
  }

  private writePersistedUserModules(modules: readonly UserModule[]): void {
    try {
      localStorage.setItem(STORAGE_KEY_USER_MODULES, JSON.stringify(modules));
    } catch {
      /* ignore */
    }
  }

  private clearPersistedSession(): void {
    try {
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(STORAGE_KEY_USER_MODULES);
    } catch {
      /* ignore */
    }
  }

  private toErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as { message?: string; error_description?: string; error?: string } | null;
      return body?.error_description ?? body?.message ?? body?.error ?? `Request failed (${err.status})`;
    }
    if (err instanceof Error) return err.message;
    return 'Unable to sign in. Please try again.';
  }
}
